import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Lock, RefreshCw, Smartphone, KeyRound, QrCode, ChevronLeft, AlertCircle
} from 'lucide-react';

const KIOSK_TOKEN_KEY = 'gl_kiosk_token';
const RESULT_TIMEOUT_S = 60;

function getKioskToken() {
  try { return localStorage.getItem(KIOSK_TOKEN_KEY); } catch (_) { return null; }
}
function setKioskToken(token) {
  try {
    if (token) localStorage.setItem(KIOSK_TOKEN_KEY, token);
    else localStorage.removeItem(KIOSK_TOKEN_KEY);
  } catch (_) { /* ignore */ }
}

/**
 * Self-service kiosk: a locked-down, full-screen flow for a tablet at the
 * front desk. A dedicated `kiosk` backend role can ONLY allocate lockers and
 * read availability, so the device being public is not a risk.
 *
 * Flow: Get a locker -> pick an area -> huge locker number + PIN with
 * instructions to scan the room QR and link the phone. Auto-resets for the
 * next guest after 60 s.
 */
const Kiosk = () => {
  const [token, setToken] = useState(getKioskToken);
  // 'login' | 'idle' | 'pick' | 'result'
  const [step, setStep] = useState(token ? 'idle' : 'login');

  // login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // data
  const [groups, setGroups] = useState([]);
  const [counts, setCounts] = useState({});
  const [busyGroup, setBusyGroup] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { locker, pin, expires_at }
  const [resetIn, setResetIn] = useState(RESULT_TIMEOUT_S);
  const resetTimer = useRef(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getKioskToken()}` }),
    []
  );

  const handleAuthFailure = useCallback(() => {
    setKioskToken(null);
    setToken(null);
    setStep('login');
  }, []);

  const fetchAvailability = useCallback(async () => {
    try {
      const [groupsRes, lockersRes] = await Promise.all([
        axios.get('/api/groups', { headers: authHeaders() }),
        axios.get('/api/lockers', { headers: authHeaders() })
      ]);
      const groupsData = groupsRes.data || [];
      const lockers = lockersRes.data || [];
      const next = {};
      groupsData.forEach((g) => {
        const ids = g.locker_ids || [];
        next[g.id] = lockers.filter(
          (l) => ids.includes(l.id) && l.status === 'available' && l.is_online === 1
        ).length;
      });
      setGroups(groupsData);
      setCounts(next);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        handleAuthFailure();
      }
    }
  }, [authHeaders, handleAuthFailure]);

  // Refresh availability while the picker is visible
  useEffect(() => {
    if (step !== 'pick' && step !== 'idle') return undefined;
    fetchAvailability();
    const t = setInterval(fetchAvailability, 15000);
    return () => clearInterval(t);
  }, [step, fetchAvailability]);

  // Auto-reset countdown on the result screen
  useEffect(() => {
    if (step !== 'result') return undefined;
    setResetIn(RESULT_TIMEOUT_S);
    resetTimer.current = setInterval(() => {
      setResetIn((s) => {
        if (s <= 1) {
          clearInterval(resetTimer.current);
          setResult(null);
          setStep('idle');
          return RESULT_TIMEOUT_S;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(resetTimer.current);
  }, [step]);

  const login = async (e) => {
    if (e) e.preventDefault();
    if (loginBusy) return;
    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await axios.post('/api/auth/login', { username, password });
      setKioskToken(res.data.token);
      setToken(res.data.token);
      setPassword('');
      setStep('idle');
    } catch (err) {
      setLoginError(err?.response?.data?.error || 'Sign-in failed');
    } finally {
      setLoginBusy(false);
    }
  };

  const assign = async (group) => {
    if (busyGroup) return;
    setBusyGroup(group.id);
    setError(null);
    try {
      const res = await axios.post(
        '/api/lockers/assign-cardless',
        { group_id: group.id },
        { headers: authHeaders() }
      );
      setResult(res.data);
      setStep('result');
      fetchAvailability();
    } catch (err) {
      if (err?.response?.status === 401) {
        handleAuthFailure();
        return;
      }
      setError(err?.response?.data?.error || 'Could not assign a locker. Please see the front desk.');
    } finally {
      setBusyGroup(null);
    }
  };

  // ===== login =====
  if (step === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <form onSubmit={login} className="glass-sheet p-6 w-full max-w-sm animate-fade-up">
          <div className="text-center mb-5">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-b from-primary-400 to-primary-600 flex items-center justify-center shadow-glow">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-title2 text-white">Kiosk setup</h1>
            <p className="text-footnote text-secondary mt-1">
              One-time sign-in with the kiosk account.
            </p>
          </div>
          <label className="block text-footnote text-label mb-1.5">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input mb-3"
            autoComplete="username"
          />
          <label className="block text-footnote text-label mb-1.5">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input mb-4"
            autoComplete="current-password"
          />
          {loginError && (
            <p className="text-subheadline text-danger-300 mb-3">{loginError}</p>
          )}
          <button type="submit" disabled={loginBusy} className="btn btn-primary w-full py-3.5">
            {loginBusy ? 'Signing in...' : 'Start kiosk'}
          </button>
        </form>
      </div>
    );
  }

  // ===== result =====
  if (step === 'result' && result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-lg text-center animate-pop">
          <p className="text-footnote text-label uppercase tracking-widest mb-2">Your locker</p>
          <div className="text-[110px] leading-none font-bold text-white tracking-tight mb-5">
            {result.locker?.name}
          </div>

          {result.pin && (
            <div className="glass-sheet p-6 mb-5">
              <div className="flex items-center justify-center space-x-1.5 mb-2">
                <KeyRound className="w-4 h-4 text-primary-300" />
                <p className="text-footnote text-label">Your PIN - remember it</p>
              </div>
              <div
                className="text-6xl font-extrabold tracking-[0.3em] text-primary-300 mb-5"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {result.pin}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                <div className="material-thin rounded-xl p-3.5 flex items-start space-x-3">
                  <QrCode className="w-5 h-5 text-primary-300 flex-shrink-0 mt-0.5" />
                  <p className="text-footnote text-secondary">
                    Scan the QR code posted in the locker room with your phone.
                  </p>
                </div>
                <div className="material-thin rounded-xl p-3.5 flex items-start space-x-3">
                  <Smartphone className="w-5 h-5 text-primary-300 flex-shrink-0 mt-0.5" />
                  <p className="text-footnote text-secondary">
                    Enter this PIN once. Your phone links to locker{' '}
                    {result.locker?.name} - then swipe to unlock.
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => { setResult(null); setStep('idle'); }}
            className="btn btn-secondary w-full py-3.5"
          >
            Done
          </button>
          <p className="text-caption text-muted mt-3">
            This screen clears in {resetIn}s
          </p>
        </div>
      </div>
    );
  }

  // ===== area picker =====
  if (step === 'pick') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-lg animate-fade-up">
          <button
            onClick={() => setStep('idle')}
            className="flex items-center space-x-1 text-secondary text-subheadline mb-5"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-title1 text-white mb-1">Pick your area</h1>
          <p className="text-secondary text-subheadline mb-6">
            A free locker is assigned automatically.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {groups.map((g) => {
              const available = counts[g.id] || 0;
              const enabled = available > 0;
              return (
                <button
                  key={g.id}
                  disabled={!enabled || busyGroup !== null}
                  onClick={() => assign(g)}
                  className={`p-6 rounded-2xl border text-left transition-all ${
                    enabled
                      ? 'material-thin hover:border-primary-500/50 hover:bg-white/[0.08] active:scale-[0.98]'
                      : 'border-white/[0.05] bg-white/[0.02] opacity-50'
                  }`}
                >
                  <div className="text-title2 text-white mb-1 flex items-center">
                    {busyGroup === g.id && (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2 text-primary-300" />
                    )}
                    {g.name}
                  </div>
                  <div className={`text-subheadline ${enabled ? 'text-secondary' : 'text-danger-300'}`}>
                    {enabled ? `${available} lockers free` : 'Full right now'}
                  </div>
                </button>
              );
            })}
          </div>
          {error && (
            <div className="mt-5 material-regular rounded-2xl p-4 flex items-start space-x-3 border-danger-500/30">
              <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              <p className="text-subheadline text-danger-300">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== idle =====
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center animate-fade-up">
        <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-b from-primary-400 to-primary-600 flex items-center justify-center shadow-glow">
          <Lock className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-largetitle text-white mb-2">Welcome to INZAN</h1>
        <p className="text-secondary text-body mb-10">
          Get a locker in seconds - no card needed. Your phone becomes the key.
        </p>
        <button
          onClick={() => setStep('pick')}
          className="btn btn-primary w-full py-5 text-title3"
        >
          Get a locker
        </button>
      </div>
    </div>
  );
};

export default Kiosk;
